import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";
import { useEffect, useMemo, useState } from "react";
import VolumesAPI from "../../../API/VolumesAPI";
import Header from "../../Header";
import LoadingSpinner from "../../Shared/LoadingSpinner";
import { bundleSetup, calcBundle, defaultSetup, groupProducts } from "./calcBundle";
import { DEFAULT_SHORTFALL, DEFAULT_SPRAYS, PACK_OPTIONS } from "./bundleConfig";
import "./ResistanceVolume.css";

const money = (v) => (v === null || v === undefined ? "" : `R ${Math.round(v).toLocaleString("en-ZA").replace(/,/g, " ")}`);
const price = (v) => (v === null || v === undefined || v === 0 ? "" : `R ${v.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const qty = (v) => (v ? v.toLocaleString("en-ZA", { maximumFractionDigits: 1 }) : "");
const num3 = (v) => v.toLocaleString("en-ZA", { maximumFractionDigits: 3 });
const pct = (v) => (v === null || v === undefined ? "" : `${Math.round(v * 100)}%`);
const newKey = () => crypto.randomUUID();

const ResistanceVolume = (props) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shortfallPct, setShortfallPct] = useState(DEFAULT_SHORTFALL * 100);
  const [main, setMain] = useState({ rowId: null, boxes: null, dose: null, sprays: DEFAULT_SPRAYS });
  const [partners, setPartners] = useState([]);
  const [defaultsFound, setDefaultsFound] = useState(true);
  const [bundlesFailed, setBundlesFailed] = useState(false);
  const [doseHints, setDoseHints] = useState(new Map()); // productId -> dose per spray, from the server's bundles
  const [showCalc, setShowCalc] = useState(true);
  const [bundles, setBundles] = useState([]); // every bundle the server knows (main product -> partner groups)

  // Reload when the crop selector changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    // The prices are needed; the default bundle is a convenience, so its failure does not stop the screen.
    const bundlesCall = VolumesAPI.getResistanceBundles(props.cropId).catch(() => null);
    Promise.all([VolumesAPI.getVolumePricing(props.cropId), bundlesCall])
      .then(([data, bundles]) => {
        if (cancelled) return;
        setRows(data);
        const d = defaultSetup(data, bundles, newKey);
        setMain(d.main);
        setPartners(d.partners);
        setShortfallPct(Math.round(d.shortfall * 1000) / 10);
        setDoseHints(d.doseHints);
        setBundles(Array.isArray(bundles) ? bundles : []);
        setDefaultsFound(d.found);
        setBundlesFailed(bundles === null);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [props.cropId]);

  const groups = useMemo(() => groupProducts(rows), [rows]);
  const groupById = useMemo(() => new Map(groups.map((g) => [g.productId, g])), [groups]);
  const mainRow = rows.find((r) => r.id === main.rowId) || null;

  const result = useMemo(
    () => calcBundle(rows, { main, partners, shortfall: (shortfallPct ?? 0) / 100 }),
    [rows, main, partners, shortfallPct]
  );

  const mainOptions = useMemo(
    () => groups.flatMap((g) => g.packs).map((r) => ({ label: r.product_pack_name || r.product_name, value: r.id })),
    [groups]
  );
  const partnerOptions = (p) =>
    groups
      .filter((g) => g.productId === p.productId || (g.productId !== mainRow?.product_id && !partners.some((x) => x.productId === g.productId)))
      .map((g) => ({ label: g.name, value: g.productId }));

  const updateMain = (patch) => setMain((m) => ({ ...m, ...patch }));
  const pickMainProduct = (rowId) => {
    // Cleared: nothing to price, so the partners and the main product's own dose are cleared as well.
    if (!rowId) {
      setMain({ rowId: null, boxes: null, dose: null, sprays: DEFAULT_SPRAYS });
      setPartners([]);
      return;
    }
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    // Another pack of the same product: keep everything the user has set up.
    if (mainRow && mainRow.product_id === row.product_id) {
      updateMain({ rowId });
      return;
    }
    // A different product: load its own bundle (dose, sprays, partners, shortfall) from the database.
    const setup = bundleSetup(rows, bundles, row.product_id, newKey);
    if (setup.found) {
      setMain((m) => ({ rowId, boxes: m.boxes ?? 1, dose: setup.dose, sprays: setup.sprays }));
      setPartners(setup.partners);
      setShortfallPct(Math.round(setup.shortfall * 1000) / 10);
    } else {
      const hint = doseHints.get(row.product_id);
      setMain((m) => ({ rowId, boxes: m.boxes ?? 1, dose: hint ?? null, sprays: DEFAULT_SPRAYS }));
      setPartners([]);
    }
  };
  const updatePartner = (key, patch) => setPartners((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  const pickPartnerProduct = (key, productId) => {
    const hint = doseHints.get(productId);
    updatePartner(key, { productId, ...(hint !== undefined ? { dose: hint } : {}) });
  };
  const addPartner = () => setPartners((ps) => [...ps, { key: newKey(), productId: null, dose: null, sprays: DEFAULT_SPRAYS, packMode: "auto" }]);
  const removePartner = (key) => setPartners((ps) => ps.filter((p) => p.key !== key));

  if (loading) return <LoadingSpinner text="Laai pryse..." />;

  const { totals, lines, hectares, mainLitres, warnings, needs = {} } = result;
  const perHa = (main.dose ?? 0) * (main.sprays ?? 0);
  const perHa0 = perHa;
  // The main product's dose is fixed by the configuration; it is only editable when the database has none for the product.
  const mainDoseLocked = !!mainRow && doseHints.has(mainRow.product_id);
  const mainLine = lines.find((l) => l.group === "main");
  const packText = (l) => {
    if (l.group === "main") return "";
    const p = partners.find((x) => x.key === l.group);
    const g = p && p.productId ? groupById.get(p.productId) : null;
    if (!g) return "";
    if (g.packs.length < 2) return "Een pakgrootte";
    return p.packMode === "auto" ? "Outo" : "Groot";
  };

  return (
    <div className="weerstand-volume-view">
      <Header title="WEERSTAND VOLUME" backPath="pastOrders" setPath={props.setPath} />
      <main className="weerstand-volume-content container wv-page">
        {error && <Message severity="error" text={error} className="wv-message" />}
        {bundlesFailed && (
          <Message severity="warn" className="wv-message" text="Kon nie die standaard bundel laai nie. Kies jou eie produkte." />
        )}
        {!bundlesFailed && !defaultsFound && (
          <Message severity="info" className="wv-message" text="Daar is geen standaard bundel vir hierdie gewas nie. Kies jou eie produkte." />
        )}
        {warnings.map((w) => (
          <Message key={w} severity="warn" className="wv-message" text={w} />
        ))}
        <Tooltip target=".wv-tip" />

        <div className="wv-top">
          <div className="wv-card wv-inputs">
            <div className="wv-card-title">Insette</div>

            <div className="wv-main-grid">
              <div className="wv-mini wv-mini-product">
                <label>Hoofproduk</label>
                <Dropdown value={main.rowId} options={mainOptions} onChange={(e) => pickMainProduct(e.value)} placeholder="Kies produk" filter showClear />
              </div>
              <div className="wv-mini">
                <label htmlFor="wv-boxes">Bokse</label>
                <InputNumber inputId="wv-boxes" value={main.boxes} onValueChange={(e) => updateMain({ boxes: e.value })} min={0} useGrouping={false} disabled={!main.rowId} />
              </div>
              <div className="wv-mini">
                <label htmlFor="wv-dose">Dosis per spuit</label>
                <InputNumber
                  inputId="wv-dose"
                  value={main.dose}
                  onValueChange={(e) => updateMain({ dose: e.value })}
                  min={0}
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  disabled={mainDoseLocked}
                />
              </div>
              <div className="wv-mini">
                <label htmlFor="wv-sprays">Spuite</label>
                <InputNumber inputId="wv-sprays" value={main.sprays} onValueChange={(e) => updateMain({ sprays: e.value })} min={1} max={12} useGrouping={false} />
              </div>
              <div className="wv-mini wv-mini-calc">
                <label>Dosis na alle spuite</label>
                <span className="wv-calc">{perHa ? `${num3(perHa)} per ha` : "–"}</span>
              </div>
              <div className="wv-mini">
                <label htmlFor="wv-short">Toelaatbare tekort</label>
                <InputNumber inputId="wv-short" value={shortfallPct} onValueChange={(e) => setShortfallPct(e.value ?? 0)} min={0} max={20} suffix=" %" minFractionDigits={0} maxFractionDigits={1} />
              </div>
              <div className="wv-mini wv-mini-calc">
                <label>Lys prys (R/L)</label>
                <span className="wv-calc">{mainLine ? price(mainLine.listPrice) || "–" : "–"}</span>
              </div>
              <div className="wv-mini wv-mini-calc">
                <label>MyPack prys (R/L)</label>
                <span className="wv-calc">{mainLine ? price(mainLine.price) || "–" : "–"}</span>
              </div>
            </div>

            <div className="wv-card-title wv-subtitle">Vennoot-produkte</div>
            {!main.rowId && <p className="wv-hint">Kies eers &apos;n hoofproduk.</p>}
            {main.rowId && partners.length === 0 && <p className="wv-hint">Geen vennote nie. Voeg produkte by wat saam met die hoofproduk gekoop word.</p>}
            {main.rowId &&
              partners.map((p) => {
                const g = p.productId ? groupById.get(p.productId) : null;
                const multiPack = g && g.packs.length > 1;
                const n = needs[p.key];
                const unit = g && /kg/i.test(g.packs[0].product_pack_name || "") ? "kg" : "L";
                const has = n && n.total > 0;
                const pPerHa = (p.dose ?? 0) * (p.sprays ?? 0);
                const needTip = has
                  ? `${num3(pPerHa)} ${unit} per ha (${num3(p.dose)} ${unit} x ${p.sprays} spuite) x ${qty(hectares)} ha = ${num3(n.total)} ${unit} nodig`
                  : "";
                const haTip = hectares > 0 ? `Hoofproduk: ${qty(mainLitres)} L ÷ ${num3(perHa0)} L/ha = ${qty(hectares)} ha. Elke vennoot moet presies dieselfde hektaar dek.` : "";
                return (
                  <div className="wv-partner" key={p.key}>
                    <div className="wv-partner-main">
                      <div className="wv-mini wv-mini-product">
                        <label>Vennoot</label>
                        <Dropdown value={p.productId} options={partnerOptions(p)} onChange={(e) => pickPartnerProduct(p.key, e.value)} placeholder="Kies vennoot" filter />
                      </div>
                      <div className="wv-mini">
                        <label>Dosis per spuit</label>
                        <InputNumber value={p.dose} onValueChange={(e) => updatePartner(p.key, { dose: e.value })} min={0} minFractionDigits={0} maxFractionDigits={3} />
                      </div>
                      <div className="wv-mini">
                        <label>Spuite</label>
                        <InputNumber value={p.sprays} onValueChange={(e) => updatePartner(p.key, { sprays: e.value })} min={1} max={12} useGrouping={false} />
                      </div>
                      <div className="wv-mini wv-mini-calc">
                        <label>Dosis na alle spuite</label>
                        <span className="wv-calc">{pPerHa ? `${num3(pPerHa)} per ha` : "–"}</span>
                      </div>
                      <div className="wv-mini wv-mini-wide">
                        <label>Verpakking</label>
                        {multiPack ? (
                          <Dropdown value={p.packMode} options={PACK_OPTIONS} onChange={(e) => updatePartner(p.key, { packMode: e.value })} />
                        ) : (
                          <span className="wv-calc wv-calc-plain">{g ? "Een pakgrootte" : ""}</span>
                        )}
                      </div>
                      <Button icon="pi pi-times" className="p-button-text p-button-sm" onClick={() => removePartner(p.key)} aria-label="Verwyder" />
                    </div>
                    <div className="wv-partner-calc">
                      <span>
                        Totaal nodig:{" "}
                        <strong className="wv-tip" data-pr-tooltip={needTip} data-pr-position="top">
                          {has ? `${qty(n.total)} ${unit}` : "–"}
                        </strong>
                        {has ? ` (${n.sprays} spuite x ${qty(n.perSpray)})` : ""}
                      </span>
                      <span>
                        Moet dek:{" "}
                        <strong className="wv-tip" data-pr-tooltip={haTip} data-pr-position="top">
                          {hectares > 0 ? `${qty(hectares)} ha` : "–"}
                        </strong>{" "}
                        (dieselfde as hoofproduk)
                      </span>
                    </div>
                  </div>
                );
              })}
            {main.rowId && (
              <div>
                <Button label="Voeg vennoot by" icon="pi pi-plus" className="p-button-outlined" onClick={addPartner} />
              </div>
            )}
          </div>

          <div className="wv-card wv-summary">
            <div>
              <span>{mainRow ? mainRow.product_name : "Hoofproduk"} liter</span>
              <strong>{qty(mainLitres) || "0"} L</strong>
            </div>
            <div>
              <span>Hektaar gedek</span>
              <strong>{qty(hectares) || "0"} ha</strong>
            </div>
            <div>
              <span>Totale koste</span>
              <strong>{money(totals.cost)}</strong>
            </div>
            <div>
              <span>Koste per ha</span>
              <strong>{money(totals.costPerHa)}</strong>
            </div>
            <p className="wv-hint">
              Hektaar = liter ÷ ({main.dose ?? 0} L/ha x {main.sprays ?? 0} spuite) = liter ÷ {perHa ? perHa.toLocaleString("en-ZA", { maximumFractionDigits: 3 }) : 0} L/ha.
            </p>
          </div>
        </div>

        <div className="wv-table-wrap">
          <table className="wv-table">
            <thead>
              <tr>
                <th>Produk</th>
                <th>Verpakking</th>
                <th>Bokse</th>
                <th>Liter / kg</th>
                <th>Hektaar gedek</th>
                <th>Dekking %</th>
                <th>Lys prys (R/L)</th>
                <th>MyPack prys (R/L)</th>
                <th>Lys koste</th>
                <th>MyPack koste</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.key} className={l.boxes === 0 ? "wv-zero" : ""}>
                  <td>{l.label}</td>
                  <td>{packText(l)}</td>
                  <td className="num">{l.boxes || ""}</td>
                  <td className="num">{qty(l.litres)}</td>
                  <td className="num">{l.boxes ? qty(l.haCovered) : ""}</td>
                  <td className="num">{pct(l.coverPct)}</td>
                  <td className="num">{price(l.listPrice)}</td>
                  <td className="num">{price(l.price)}</td>
                  <td className="num">{l.boxes ? money(l.listCost) : ""}</td>
                  <td className="num">{l.boxes ? money(l.cost) : ""}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Totaal</td>
                <td />
                <td className="num">{totals.boxes || ""}</td>
                <td colSpan={5} />
                <td className="num">{money(totals.listCost)}</td>
                <td className="num">{money(totals.cost)}</td>
              </tr>
              <tr>
                <td colSpan={8} className="num">
                  Koste per ha
                </td>
                <td className="num">{money(totals.listCostPerHa)}</td>
                <td className="num">{money(totals.costPerHa)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="wv-calc-head">
          <Button
            label={showCalc ? "Versteek berekening" : "Wys berekening"}
            icon={showCalc ? "pi pi-chevron-up" : "pi pi-chevron-down"}
            className="p-button-text"
            onClick={() => setShowCalc((v) => !v)}
          />
        </div>
        {showCalc && (
          <div className="wv-table-wrap wv-workings">
            <div className="wv-card-title wv-workings-title">Berekening (hoe die bokse bepaal word)</div>
            <table className="wv-table">
              <thead>
                <tr>
                  <th>Produk</th>
                  <th>Eenheid (L/kg)</th>
                  <th>Eenhede per boks</th>
                  <th>Liter per boks</th>
                  <th>Dosis per spuit</th>
                  <th>Spuite</th>
                  <th>Hektaar per boks</th>
                  <th>Verhouding</th>
                  <th>Bokse</th>
                  <th>Liter</th>
                  <th>Liter nodig</th>
                  <th>Hektaar gedek</th>
                  <th>Oorskot (ha)</th>
                  <th>Dekking %</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.key} className={l.boxes === 0 ? "wv-zero" : ""}>
                    <td>{l.label}</td>
                    <td className="num">{qty(l.unitVolume)}</td>
                    <td className="num">{l.unitsPerBox || ""}</td>
                    <td className="num">{qty(l.packVolume)}</td>
                    <td className="num">{l.dose ? num3(l.dose) : ""}</td>
                    <td className="num">{l.sprays || ""}</td>
                    <td className="num">{l.haPerBox ? qty(l.haPerBox) : ""}</td>
                    <td className="num">{l.ratio ? num3(l.ratio) : ""}</td>
                    <td className="num">{l.boxes || ""}</td>
                    <td className="num">{l.boxes ? qty(l.litres) : ""}</td>
                    <td className="num">{l.firstOfGroup && l.need ? qty(l.need) : ""}</td>
                    <td className="num">{l.boxes ? qty(l.haCovered) : ""}</td>
                    <td className="num">{l.surplusHa !== null && l.surplusHa !== undefined ? qty(l.surplusHa) : ""}</td>
                    <td className="num">{pct(l.coverPct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="wv-hint wv-workings-hint">
              Hektaar per boks = liter per boks ÷ (dosis x spuite). Verhouding = hektaar van een boks hoofproduk ÷ hektaar van een boks van hierdie produk. Liter nodig = hektaar x dosis x spuite.
              Bokse = liter nodig, minus die toelaatbare tekort, opwaarts afgerond op volle bokse.
            </p>
          </div>
        )}

        <p className="wv-note">
          Beraming. Bokse word opwaarts afgerond; die toelaatbare tekort laat toe dat &apos;n produk effens minder as die presiese hoeveelheid dek voordat nog &apos;n boks bygevoeg word. Dekking bo
          100% beteken &apos;n oorskot. Pryse is die weerstandsprys (of seisoenprys). Dit geld as elke vennoot genoeg hektaar dek (sien Dekking %). Verpakking word by die insette gekies.
        </p>
      </main>
    </div>
  );
};

export default ResistanceVolume;
